import { useCallback, useEffect, useState } from 'react';

/**
 * Tema dell'interfaccia: chiaro, scuro, o quello che dice il browser.
 *
 * <h2>Perche' tre stati e non un interruttore</h2>
 *
 * Un interruttore a due posizioni deve pur partire da qualcosa, e qualunque cosa scelga contraddice
 * l'utente che ha configurato il sistema: chi tiene il computer in scuro e apre un portale chiaro non
 * pensa "ecco il valore predefinito", pensa che il tema scuro non ci sia. `system` e' quindi il valore
 * iniziale, e le altre due sono scelte esplicite che vincono sul browser.
 *
 * <h2>Perche' l'attributo e non una classe</h2>
 *
 * `data-theme` sull'elemento radice ha tre stati distinguibili in CSS: assente (segui il browser),
 * `light`, `dark`. Con una classe `dark` si potrebbe solo dire "scuro si' o no", e la terza
 * possibilita' - "non ho deciso, decidi tu" - non sarebbe rappresentabile. E' la stessa ragione per cui
 * qui non si legge `prefers-color-scheme` in JavaScript per poi scrivere una classe: quel valore puo'
 * cambiare mentre la pagina e' aperta, e il CSS lo segue da se'.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'mme.theme';

function leggiScelta(): ThemeChoice {
  const salvato = window.localStorage.getItem(STORAGE_KEY);
  return salvato === 'light' || salvato === 'dark' ? salvato : 'system';
}

function applica(scelta: ThemeChoice): void {
  const radice = document.documentElement;
  if (scelta === 'system') {
    radice.removeAttribute('data-theme');
  } else {
    radice.setAttribute('data-theme', scelta);
  }
}

export function useTheme() {
  const [scelta, setScelta] = useState<ThemeChoice>(leggiScelta);

  useEffect(() => {
    applica(scelta);
  }, [scelta]);

  const cambia = useCallback((nuova: ThemeChoice) => {
    if (nuova === 'system') {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, nuova);
    }
    setScelta(nuova);
  }, []);

  return { scelta, cambia };
}

/**
 * Applica il tema salvato **prima** del primo disegno.
 *
 * Si chiama dal punto di ingresso e non da un componente: dentro React il primo effetto gira dopo il
 * primo rendering, e per quel fotogramma la pagina resta chiara. Su un tema scuro e' un lampo bianco
 * a schermo intero, il difetto piu' visibile di tutta questa slice.
 */
export function applicaTemaSalvato(): void {
  applica(leggiScelta());
}
